import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Navigate, useSearchParams } from "react-router-dom";
import { Shield, Gift, XCircle, Search, UserCheck, Instagram, Activity, RefreshCw, CheckCircle2, AlertTriangle, Webhook, FileText } from "lucide-react";
import AdminBlog from "../components/AdminBlog";

export default function Admin() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const initialTab = ["users", "webhooks", "blog"].includes(params.get("tab")) ? params.get("tab") : "users";
  const [tab, setTabState] = useState(initialTab);
  const setTab = (next) => {
    setTabState(next);
    setParams(next === "users" ? {} : { tab: next }, { replace: true });
  };
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [grantTier, setGrantTier] = useState("pro");
  const [grantNote, setGrantNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [tokenStatus, setTokenStatus] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, u] = await Promise.all([api.get("/admin/stats"), api.get("/admin/users")]);
      setStats(s.data);
      setUsers(u.data);
    } catch (err) {
      console.error("Admin load failed:", err);
      toast.error("Could not load admin data");
    } finally { setLoading(false); }
  }, []);

  const loadEvents = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/webhook-events?limit=50");
      setEvents(data);
    } catch (err) { console.error("Webhook events load failed:", err); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (tab !== "webhooks") return;
    loadEvents();
    const id = setInterval(loadEvents, 5000); // auto-refresh every 5s
    return () => clearInterval(id);
  }, [tab, loadEvents]);

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const grant = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${selected.id}/grant-plan`, { tier: grantTier, note: grantNote });
      toast.success(`Granted ${grantTier} to ${selected.email}`);
      setSelected(null);
      setGrantNote("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Grant failed");
    } finally { setBusy(false); }
  };

  const revoke = async (u) => {
    if (!confirm(`Revoke admin-granted plan from ${u.email}?`)) return;
    try {
      await api.post(`/admin/users/${u.id}/revoke-plan`);
      toast.success(`Revoked plan from ${u.email}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Revoke failed");
    }
  };

  const filtered = users.filter((u) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return (
      u.email?.toLowerCase().includes(needle) ||
      u.full_name?.toLowerCase().includes(needle) ||
      u.ig_username?.toLowerCase().includes(needle)
    );
  });

  return (
    <div className="p-8 md:p-12 max-w-[1400px]">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
        <div>
          <div className="overline mb-3 flex items-center gap-2"><Shield className="w-3.5 h-3.5" /> Admin console</div>
          <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter leading-none" data-testid="admin-title">
            Platform.
          </h1>
          <p className="mt-3 text-white/60">Grant plans to testers, monitor signups, track MRR.</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-12">
        <AdminStat label="Total users" value={stats?.total_users} testid="admin-stat-users" />
        <AdminStat label="Active subs" value={stats?.active_subscriptions} testid="admin-stat-active" />
        <AdminStat label="Paid subs" value={stats?.paid_subscriptions} testid="admin-stat-paid" />
        <AdminStat label="MRR estimate" value={stats ? `$${stats.mrr_estimate_usd.toFixed(0)}` : "—"} testid="admin-stat-mrr" />
        <AdminStat label="Admin grants" value={stats?.admin_grants} testid="admin-stat-grants" />
        <AdminStat label="IG connected" value={stats?.ig_connected} testid="admin-stat-ig" />
        <AdminStat label="Total flows" value={stats?.total_keywords} testid="admin-stat-keywords" />
        <AdminStat label="Total DMs" value={stats?.total_messages} testid="admin-stat-messages" />
      </div>

      {/* Tabs */}
      <div className="mb-6 flex items-center gap-1 border-b border-line">
        <button
          onClick={() => setTab("users")}
          className={`px-5 py-3 text-xs font-bold uppercase tracking-wider transition-colors ${tab === "users" ? "text-cyan border-b-2 border-cyan" : "text-white/50 hover:text-white"}`}
          data-testid="admin-tab-users"
        >Users</button>
        <button
          onClick={() => setTab("webhooks")}
          className={`px-5 py-3 text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-2 ${tab === "webhooks" ? "text-cyan border-b-2 border-cyan" : "text-white/50 hover:text-white"}`}
          data-testid="admin-tab-webhooks"
        ><Activity className="w-3.5 h-3.5" /> Webhook activity</button>
        <button
          onClick={() => setTab("blog")}
          className={`px-5 py-3 text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-2 ${tab === "blog" ? "text-cyan border-b-2 border-cyan" : "text-white/50 hover:text-white"}`}
          data-testid="admin-tab-blog"
        ><FileText className="w-3.5 h-3.5" /> Blog</button>
      </div>

      {tab === "blog" && <AdminBlog />}

      {tab === "webhooks" && (
        <div data-testid="admin-webhooks-panel">
          <div className="surface-card p-5 mb-6 flex items-center justify-between gap-3">
            <div>
              <div className="overline mb-1">Live webhook events</div>
              <div className="text-sm text-white/60">Auto-refreshes every 5 seconds. Most recent first. Each row expands to show every processing step Meta's payload triggered in our backend.</div>
            </div>
            <div className="flex gap-2">
              <button onClick={loadEvents} className="btn-ghost text-xs" data-testid="admin-refresh-events">
                <RefreshCw className="w-3 h-3" /> Refresh
              </button>
            </div>
          </div>

          {tokenStatus && (
            <div className={`surface-card p-4 mb-6 ${tokenStatus.ok ? "border-cyan/40" : "border-rose-400/40"}`} data-testid="admin-token-status">
              {tokenStatus.loading ? (
                <div className="text-white/60 text-sm">Testing…</div>
              ) : tokenStatus.ok ? (
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-cyan shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold text-cyan">Token valid</div>
                    <div className="text-xs text-white/70 mt-1 font-mono">@{tokenStatus.body?.username} · type {tokenStatus.body?.account_type} · IG ID {tokenStatus.ig_user_id}</div>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold text-rose-400">Token invalid or expired</div>
                    <pre className="text-xs text-white/70 mt-1 overflow-x-auto">{JSON.stringify(tokenStatus, null, 2)}</pre>
                    <div className="text-xs text-white/60 mt-2">Reconnect Instagram from the dashboard.</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* IG-connected users — test any of them */}
          {users.filter((u) => u.ig_connected).length > 0 && (
            <div className="surface-card p-5 mb-6" data-testid="admin-ig-users-panel">
              <div className="overline mb-3">Instagram-connected accounts</div>
              <div className="space-y-2">
                {users.filter((u) => u.ig_connected).map((u) => (
                  <div key={u.id} className="flex items-center justify-between gap-3 py-2 border-b border-white/5 last:border-0" data-testid={`admin-ig-user-${u.id}`}>
                    <div className="flex items-center gap-3">
                      <Instagram className="w-4 h-4 text-cyan" />
                      <div>
                        <div className="text-sm font-bold flex items-center gap-2">
                          @{u.ig_username || "unknown"}
                          {u.ig_webhook_subscribed ? (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-cyan" data-testid={`admin-ig-sub-ok-${u.id}`}>
                              <CheckCircle2 className="w-3 h-3" /> Webhook on
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-rose-400" data-testid={`admin-ig-sub-bad-${u.id}`}>
                              <AlertTriangle className="w-3 h-3" /> Not subscribed
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-white/50 font-mono">{u.email} · IG ID {u.ig_user_id}</div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={async () => {
                          setTokenStatus({ loading: true });
                          try {
                            const { data } = await api.post(`/admin/test-ig-token?user_id=${u.id}`);
                            setTokenStatus(data);
                          } catch (err) {
                            setTokenStatus({ ok: false, error: err?.response?.data?.detail || String(err) });
                          }
                        }}
                        className="btn-ghost text-xs"
                        data-testid={`admin-test-token-${u.id}`}
                      >Test token</button>
                      <button
                        onClick={async () => {
                          setTokenStatus({ loading: true });
                          try {
                            const { data } = await api.post(`/admin/users/${u.id}/resubscribe-webhooks`);
                            setTokenStatus({
                              ok: data?.ok,
                              status: data?.status,
                              body: data?.body,
                              ig_user_id: u.ig_user_id,
                              _action: "resubscribe",
                            });
                            load();
                          } catch (err) {
                            setTokenStatus({ ok: false, error: err?.response?.data?.detail || String(err) });
                          }
                        }}
                        className="btn-ghost text-xs"
                        data-testid={`admin-resubscribe-${u.id}`}
                      ><Webhook className="w-3 h-3" /> Re-subscribe</button>
                      <button
                        onClick={async () => {
                          setTokenStatus({ loading: true });
                          try {
                            const { data } = await api.get(`/admin/users/${u.id}/webhook-subscription`);
                            setTokenStatus({ ...data, _action: "view-subscription", ig_user_id: u.ig_user_id });
                          } catch (err) {
                            setTokenStatus({ ok: false, error: err?.response?.data?.detail || String(err) });
                          }
                        }}
                        className="btn-ghost text-xs"
                        data-testid={`admin-view-subscription-${u.id}`}
                      >View on Meta</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-3">
            {events.length === 0 && (
              <div className="surface-card p-12 text-center text-white/50 text-sm">
                No webhook events yet. Send a DM or comment on your Instagram with a keyword to trigger one.
              </div>
            )}
            {events.map((ev) => (
              <details key={ev.id} className="surface-card p-4" data-testid={`admin-event-${ev.id}`}>
                <summary className="cursor-pointer flex items-center justify-between gap-3 list-none">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${ev.signature_valid === false ? "bg-rose-400" : "bg-cyan"}`} />
                    <span className="text-xs font-mono text-white/60">{(ev.received_at || "").slice(11, 19)}</span>
                    <span className="text-xs uppercase tracking-wider font-bold text-white/80">{ev.object || "?"}</span>
                    <span className="text-xs text-white/50">{(ev.processing || []).length} step{(ev.processing||[]).length === 1 ? "" : "s"}</span>
                  </div>
                  <span className="text-xs text-cyan font-mono">{ev.id?.slice(0, 8)}</span>
                </summary>
                <div className="mt-4 grid md:grid-cols-2 gap-4">
                  <div>
                    <div className="overline mb-2">Processing</div>
                    {(ev.processing || []).length === 0 && <div className="text-xs text-white/40">No processing steps logged.</div>}
                    {(ev.processing || []).map((p, idx) => (
                      <div key={idx} className="text-xs mb-2 pb-2 border-b border-white/5">
                        <span className="font-bold text-cyan">{p.step}</span>
                        <pre className="text-white/70 mt-1 overflow-x-auto">{JSON.stringify({ ...p, step: undefined, at: undefined }, null, 2)}</pre>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="overline mb-2">Raw payload</div>
                    <pre className="text-xs text-white/70 overflow-x-auto max-h-64">{JSON.stringify(ev.payload, null, 2)}</pre>
                  </div>
                </div>
              </details>
            ))}
          </div>
        </div>
      )}

      {tab === "users" && (
      <>

      {/* Search */}
      <div className="surface-card p-4 mb-6 flex items-center gap-3">
        <Search className="w-4 h-4 text-white/50" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by email, name or IG username…"
          className="flex-1 bg-transparent outline-none text-sm"
          data-testid="admin-search"
        />
        <span className="text-xs text-white/50">{filtered.length} / {users.length}</span>
      </div>

      {/* Users table */}
      <div className="surface-card overflow-hidden" data-testid="admin-users-table">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10 bg-bone">
              <th className="text-left py-4 px-6 text-xs font-bold uppercase tracking-wider text-white/70">User</th>
              <th className="text-left py-4 px-6 text-xs font-bold uppercase tracking-wider text-white/70">Plan</th>
              <th className="text-left py-4 px-6 text-xs font-bold uppercase tracking-wider text-white/70">Source</th>
              <th className="text-left py-4 px-6 text-xs font-bold uppercase tracking-wider text-white/70">IG</th>
              <th className="text-left py-4 px-6 text-xs font-bold uppercase tracking-wider text-white/70">Role</th>
              <th className="text-right py-4 px-6 text-xs font-bold uppercase tracking-wider text-white/70">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="py-12 text-center text-white/50 text-sm">Loading…</td></tr>
            )}
            {!loading && filtered.map((u) => {
              const isAdminGrant = u.subscription_source === "admin_grant";
              return (
                <tr key={u.id} className="border-b border-white/5 hover:bg-surface/5 transition-colors" data-testid={`admin-user-row-${u.id}`}>
                  <td className="py-4 px-6">
                    <div className="font-semibold text-sm">{u.full_name || "—"}</div>
                    <div className="text-xs text-white/60 font-mono">{u.email}</div>
                  </td>
                  <td className="py-4 px-6">
                    <span className={`inline-block px-2 py-1 text-[10px] uppercase tracking-wider font-bold ${
                      u.subscription_status === "active" ? "bg-klein/10 text-klein" : "bg-surface/10 text-white/60"
                    }`}>
                      {u.subscription_tier || "free"} · {u.subscription_status || "inactive"}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-xs">
                    {isAdminGrant ? (
                      <span className="inline-flex items-center gap-1 text-cyan font-semibold"><Gift className="w-3 h-3" /> Granted</span>
                    ) : u.stripe_subscription_id ? (
                      <span className="text-white/70 font-mono">Stripe</span>
                    ) : (
                      <span className="text-white/40">—</span>
                    )}
                  </td>
                  <td className="py-4 px-6 text-xs">
                    {u.ig_connected ? (
                      <span className="inline-flex items-center gap-1 text-klein font-mono"><Instagram className="w-3 h-3" />@{u.ig_username || "—"}</span>
                    ) : <span className="text-white/40">—</span>}
                  </td>
                  <td className="py-4 px-6 text-xs">
                    {u.role === "admin" ? (
                      <span className="inline-flex items-center gap-1 text-white font-bold uppercase tracking-wider"><Shield className="w-3 h-3" /> Admin</span>
                    ) : <span className="text-white/60">user</span>}
                  </td>
                  <td className="py-4 px-6 text-right whitespace-nowrap">
                    <button
                      onClick={() => { setSelected(u); setGrantTier(u.subscription_tier === "free" ? "pro" : u.subscription_tier); }}
                      className="btn-ghost text-xs py-1.5 px-3 mr-1"
                      data-testid={`admin-grant-${u.id}`}
                    >
                      <Gift className="w-3 h-3" /> Grant
                    </button>
                    {isAdminGrant && (
                      <button
                        onClick={() => revoke(u)}
                        className="btn-ghost text-xs py-1.5 px-3 text-rose-400"
                        data-testid={`admin-revoke-${u.id}`}
                      >
                        <XCircle className="w-3 h-3" /> Revoke
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="py-12 text-center text-white/50 text-sm">No users match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      </>
      )}

      {/* Grant modal */}
      {selected && (
        <div
          className="fixed inset-0 z-50 bg-obsidian/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
          data-testid="admin-grant-modal"
        >
          <div onClick={(e) => e.stopPropagation()} className="bg-bone border border-white/10 p-8 w-full max-w-md shadow-2xl">
            <div className="overline mb-1 flex items-center gap-2"><UserCheck className="w-3.5 h-3.5" /> Grant free plan</div>
            <h2 className="font-heading font-black text-2xl tracking-tighter mb-1">{selected.full_name || "Anonymous"}</h2>
            <div className="font-mono text-xs text-white/60 mb-6">{selected.email}</div>

            <div className="space-y-5">
              <div>
                <label className="label-overline">Tier</label>
                <div className="grid grid-cols-3 gap-2">
                  {["free", "starter", "pro"].map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setGrantTier(t)}
                      className={`px-3 py-2 border text-xs font-bold uppercase tracking-wider transition-colors ${
                        grantTier === t ? "border-white bg-obsidian text-white" : "border-white/15 hover:border-white/40"
                      }`}
                      data-testid={`admin-grant-tier-${t}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="label-overline">Note (optional)</label>
                <input
                  value={grantNote}
                  onChange={(e) => setGrantNote(e.target.value)}
                  placeholder="Tester · early access · partner…"
                  className="input-field"
                  data-testid="admin-grant-note"
                />
              </div>
            </div>

            <div className="mt-8 flex justify-end gap-3">
              <button onClick={() => setSelected(null)} className="btn-ghost" data-testid="admin-grant-cancel">Cancel</button>
              <button onClick={grant} disabled={busy} className="btn-primary" data-testid="admin-grant-confirm">
                {busy ? "Granting…" : <>Grant {grantTier} plan <Gift className="w-4 h-4" /></>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminStat({ label, value, testid }) {
  return (
    <div className="surface-card p-5" data-testid={testid}>
      <div className="font-heading font-black text-3xl tracking-tighter">{value ?? "—"}</div>
      <div className="text-xs text-white/60 uppercase tracking-wider mt-1">{label}</div>
    </div>
  );
}
